import { ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';

import useLabels from '~hooks/useLabels';
import { Filter } from '~modules/media-library/model/Filter';
import {
  findInvolvedFilterGroupIds,
  findSelectChildFilterOptionIds,
  getFilterFromListById,
  getFilterOptionFromListById,
  isFilterOptionOfFilter,
} from '~modules/media-library/model/FilterHelper';
import { FilterOption } from '~modules/media-library/model/FilterOption';
import { isCheckFilter } from '~modules/media-library/model/FilterValidator';
import { Button } from '~modules/media-library/view/Button';
import FilterDropdown from '~modules/media-library/view/FilterDropdown';
import { FilterSelect } from '~modules/media-library/view/FilterSelect/FilterSelect';
import { toggleItemInPrimitiveArray } from '~services/immutable';

type FilterBarProps = {
  filterList: Filter[];
  activeFilterIds: Array<Filter['id']>;
  onChangeActiveFilterIds: (filterIds: Array<Filter['id']>) => void;
  selectedFilterValueIds: Array<FilterOption['id']>;
  onChangeSelectedFilterValueIds: (valueIds: Array<FilterOption['id']>) => void;
};

export function FilterBar({
  filterList,
  activeFilterIds = [],
  onChangeActiveFilterIds,
  selectedFilterValueIds = [],
  onChangeSelectedFilterValueIds,
}: FilterBarProps) {
  // Labels
  const { btnMediaLibraryResetFilter, btnMediaLibraryFilterSelectOpen, btnMediaLibraryFilterSelectClose } = useLabels([
    'btnMediaLibraryResetFilter',
    'btnMediaLibraryFilterSelectOpen',
    'btnMediaLibraryFilterSelectClose',
  ]);

  // Handler: on item click pass the whole (but filtered) array of selected value ids
  const handleOnItemClick = useCallback<ComponentProps<typeof FilterDropdown>['onItemClick']>(
    id => {
      const option = getFilterOptionFromListById(filterList, id);
      const selectChildIds = findSelectChildFilterOptionIds(selectedFilterValueIds, option);
      let list = [...selectedFilterValueIds];
      // disable all selected child options
      Object.values(selectChildIds).forEach(childId => {
        list = toggleItemInPrimitiveArray(list, childId);
      });
      list = toggleItemInPrimitiveArray(list, id);
      onChangeSelectedFilterValueIds(list);
    },
    [filterList, onChangeSelectedFilterValueIds, selectedFilterValueIds],
  );

  // Handler: on clear of one filter remove all matching selected values from the array and pass it up
  const makeHandleOnClear = useCallback(
    (filterId: Filter['id']) => () => {
      onChangeSelectedFilterValueIds(
        selectedFilterValueIds.filter(valueId => !isFilterOptionOfFilter(filterId, valueId)),
      );
    },
    [onChangeSelectedFilterValueIds, selectedFilterValueIds],
  );

  // Derived: calc list of filters to show to the user
  const activeFilterList = useMemo(
    () => (activeFilterIds || []).map(filterId => getFilterFromListById(filterList, filterId)),
    [filterList, activeFilterIds],
  );

  // Derived: pre compile map of selected filter values
  // This is necessary so that each FilterDropdown only gets the selectedValues that match to its filter ID
  const selectedFilterValuesMap = useMemo(() => {
    const map: Record<string, typeof selectedFilterValueIds> = {};
    for (const filterId of activeFilterIds) {
      map[filterId] = selectedFilterValueIds.filter(valueId => isFilterOptionOfFilter(filterId, valueId));
    }
    return map;
  }, [activeFilterIds, selectedFilterValueIds]);

  const involvedFilterGroups = useMemo(() => {
    const map: Record<string, typeof selectedFilterValueIds> = {};
    for (const filter of activeFilterList) {
      if (isCheckFilter(filter)) {
        map[filter.id] = findInvolvedFilterGroupIds(selectedFilterValueIds, filter.options);
      }
    }
    return map;
  }, [selectedFilterValueIds, activeFilterList]);

  return (
    <FilterSelect>
      {({ open: isEditing }) => (
        <div className="ml--filter-bar w-full flex items-center justify-between">
          <div className="ml--filter-bar--list flex gap-2 flex-wrap">
            {activeFilterList.map(filter => {
              if (isCheckFilter(filter)) {
                return (
                  <FilterDropdown
                    key={filter.id}
                    label={filter.label}
                    onItemClick={handleOnItemClick}
                    onClear={makeHandleOnClear(filter.id)}
                    options={filter.options}
                    disabled={isEditing}
                    labelClear={btnMediaLibraryResetFilter}
                    selected={selectedFilterValuesMap[filter.id] || []}
                    involvedFilterGroups={involvedFilterGroups[filter.id] || []}
                  />
                );
              }
              return null;
            })}
            {isEditing &&
              Array.from({ length: Math.max(5 - activeFilterIds.length, 0) }).map((_, idx) => (
                <Button key={`empty-${idx}`} disabled />
              ))}
          </div>
          <div className="ml--filter-bar--action">
            <FilterSelect.Button label={btnMediaLibraryFilterSelectOpen} active={isEditing} />
            <FilterSelect.Panel
              filterList={filterList}
              activeFilterIds={activeFilterIds}
              onChangeActiveFilterIds={onChangeActiveFilterIds}
              labelAction={btnMediaLibraryFilterSelectClose}
            />
          </div>
        </div>
      )}
    </FilterSelect>
  );
}
